import type { AfterViewInit, OnInit } from '@angular/core';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import type {
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormControl,
  ValidatorFn,
} from '@angular/forms';
import { FormBuilder } from '@angular/forms';
import { isNullOrUndefined } from 'is-what';
import { createNumberMask } from 'text-mask-addons/dist/textMaskAddons';
import type { Bond } from '../../../models/bond';
import { EnterBalancesValidationServiceService } from '../../services/enter-balances-validation-service.service';
import { PassdataService } from '../../services/passdata.service';

type BondForm = FormGroup<{
  id: FormControl<string>;
  cusip: FormControl<any>;
  rcrClass: FormControl<any>;
  remicClass: FormControl<any>;
  notionalFlag: FormControl<any>;
  factor: FormControl<any>;
  delay: FormControl<any>;
  principalType: FormControl<any>;
  interestType: FormControl<any>;
  interestRate: FormControl<any>;
  finalDistributionDate: FormControl<any>;
  exchangeOriginalFace: FormControl<any>;
  originalFaceToDealer: FormControl<any>;
  originalFaceToFNMA: FormControl<any>;
  currentBalanceExchanged: FormControl<any>;
}>;

@Component({
  standalone: false,
  selector: 'invd-enter-balances-recombinations-table-row',
  templateUrl: './enter-balances-recombinations-table-row.component.html',
  styleUrls: ['./enter-balances-recombinations-table-row.component.scss'],
})
export class EnterBalancesRecombinationsTableRowComponent implements OnInit, AfterViewInit {
  @Input() recombinationTableGroup: any;
  @Input() tableIndex: number;
  @Input() public recombinationTableRowExchangeType: string;
  @Input() isExternalUser: boolean;
  @Input() isFromTakedownPage: boolean;
  @Output() startedEditing = new EventEmitter<void>();

  public recombinationTableGroupCopy: any;
  public recombinationGroup: FormGroup<{ recombinationRows: FormArray<BondForm> }>;
  public recombinationRows: FormArray<BondForm>;
  public isAvailableToIncrement = false;
  public bondHasError = false;
  public rcrClass = 'rcrClass';
  public remicClass = 'remicClass';
  public isRecombinationGroupValid = false;
  public numberMask: any;

  private fb = inject(FormBuilder);
  private enterBalancesValidationService = inject(EnterBalancesValidationServiceService);
  private passData = inject(PassdataService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.recombinationTableGroupCopy = this.recombinationTableGroup;
    this.buildBonds(this.recombinationTableGroup);
    this.numberMask = createNumberMask({
      prefix: '',
      allowDecimal: true,
      decimalLimit: 2,
    });
    // Trigger change detection after building bonds to ensure table renders
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.onInitTooltipError();
    if (this.passData.getRecombinationDataCache() !== null) {
      this.resetBondControlsToDisable();
    }
    this.populateRecombinationVerbiageForExchangeType();
    this.updateFormControlsDisabledState();
    // Ensure view is updated after all initialization
    this.cdr.detectChanges();
  }

  private populateRecombinationVerbiageForExchangeType() {
    if (this.recombinationTableRowExchangeType.toString().includes('REMIC')) {
      this.recombinationTableRowExchangeType = 'REMIC';
    } else {
      this.recombinationTableRowExchangeType = 'SMBS';
    }
  }
  private createRecombinationFormBuilder(): void {
    this.recombinationGroup = this.fb.group({
      recombinationRows: this.fb.array<BondForm>([]),
    });
  }

  private buildBonds(recombinationGroup: any) {
    this.createRecombinationFormBuilder();

    recombinationGroup.recombinationRows.forEach((bond: Bond, _index: number) => {
      const bondID = this.uniqueBondID();
      const recombinationRowsControl = this.recombinationGroup.controls.recombinationRows;
      recombinationRowsControl.push(this.createBond(bond, bondID));
      recombinationRowsControl.controls.map((bondControl: BondForm) => {
        this.markBondAsPaidOff(bondControl);
      });
    });

    //this.isRecombinationGroupValidObservable();
  }

  // #TODO next release
  public isRecombinationGroupValidObservable(): void {
    this.recombinationGroup.statusChanges.subscribe((formStatus) => {
      if (formStatus === 'VALID') {
        if (this.recombinationGroup.pristine) {
          this.enterBalancesValidationService.broadCastValidTable({
            recombinationTable: this.recombinationTableGroup.recombinationNumber,
            isValid: false,
          });

          return;
        }

        this.enterBalancesValidationService.broadCastValidTable({
          recombinationTable: this.recombinationTableGroup.recombinationNumber,
          isValid: true,
        });
      } else {
        this.enterBalancesValidationService.broadCastValidTable({
          recombinationTable: this.recombinationTableGroup.recombinationNumber,
          isValid: false,
        });
      }
    });
  }

  public isScreenValid(): boolean {
    return this.isRecombinationGroupValid;
  }

  private generateBondID(): string {
    // eslint-disable-next-line no-bitwise
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).slice(1);
  }

  private uniqueBondID(): string {
    return (
      this.generateBondID() +
      this.generateBondID() +
      '-' +
      this.generateBondID() +
      '-4' +
      this.generateBondID().slice(0, 3) +
      '-' +
      this.generateBondID() +
      '-' +
      this.generateBondID() +
      this.generateBondID() +
      this.generateBondID()
    ).toLowerCase();
  }

  //#TODO set bond: any --> bond: Bond
  private createBond(bond: any, bondID: string): BondForm {
    return this.fb.group({
      id: bondID,
      cusip: [bond.cusip],
      rcrClass: [bond.rcrClass],
      remicClass: [bond.remicClass],
      notionalFlag: [bond.notionalFlag],
      factor: [bond.factor],
      delay: [bond.delayDays],
      principalType: [bond.principalType],
      interestType: [bond.interestType],
      interestRate: [bond.interestRate],
      finalDistributionDate: [bond.finalPaymentDate],
      exchangeOriginalFace: [bond.exchOrigFace],
      originalFaceToDealer: [bond.origFaceToDealer, this.bondValidator(bond.exchOrigFace)],
      originalFaceToFNMA: [bond.origFaceToFNMA, this.bondValidator(bond.exchOrigFace)],
      currentBalanceExchanged: this.repopulateCurrentBalanceExchanged(bond),
    });
  }

  private repopulateCurrentBalanceExchanged(bond: any): number {
    const exchangeTypeValue: number = bond.origFaceToDealer || bond.origFaceToFNMA;

    if (isNaN(exchangeTypeValue)) {
      return 0;
    } else {
      const computedBondValue: number =
        Math.ceil(bond.factor * exchangeTypeValue * 100 - 0.5) / 100;
      return computedBondValue;
    }
  }

  private bondValidator(exchangeOriginalFace: number): ValidatorFn {
    function isEmptyInputValue(value: any) {
      return value == null || (typeof value === 'string' && value.length === 0);
    }

    return (control: UntypedFormControl): { [s: string]: boolean } => {
      const controlValue: number = parseInt(control.value, 10);

      if (isEmptyInputValue(controlValue)) {
        return null;
      }

      if (controlValue > exchangeOriginalFace || controlValue < 0) {
        this.bondHasError = true;

        if (controlValue > exchangeOriginalFace) {
          return { invalidBondExchange: true, invalidBondExchangeTooHigh: true };
        }

        if (controlValue < 0) {
          return { invalidBondExchange: true, invalidBondExchangeTooLow: true };
        }

        return { invalidBondExchange: true };
      } else if (controlValue === exchangeOriginalFace) {
        this.bondHasError = false;
        return { invalidBondExchange: false, invalidBondExchangeTooHigh: true };
      } else if (controlValue === 0) {
        this.bondHasError = false;
        return { invalidBondExchange: false, invalidBondExchangeTooLow: true };
      } else {
        this.bondHasError = false;
      }
    };
  }

  public captureBondExchangeEvent(
    event: KeyboardEvent | FocusEvent | Event,
    currentControl: UntypedFormControl,
    indexOfCurrentControl: number,
  ): void {
    const usrInputValue = Number(this.clearSeparators(currentControl.value.toString()));
    currentControl.setValue(Math.floor(usrInputValue));
    this.startedEditing.emit();

    if (this.bondHasError) {
      //#TODO show error future hook
    } else {
      // eslint-disable-next-line deprecation/deprecation
      if ('keyCode' in event && event.keyCode === 13) {
        //currentControl.markAsPristine();
        if (usrInputValue <= 0) {
          this.resetFormGroupOnEmptyField(currentControl.parent as BondForm);
        } else {
          this.calculateBondExchanges(indexOfCurrentControl);
          this.setIsAvailableToIncrement(true);
        }
      } else if (event.type === 'blur' && currentControl.dirty) {
        //currentControl.markAsPristine();
        if (usrInputValue <= 0) {
          this.resetFormGroupOnEmptyField(currentControl.parent as BondForm);
        } else {
          this.calculateBondExchanges(indexOfCurrentControl);
          this.setIsAvailableToIncrement(true);
        }
      }
    }
  }

  private resetFormGroupOnEmptyField(currentControl: BondForm): void {
    currentControl.controls.originalFaceToDealer.enable();
    currentControl.controls.originalFaceToFNMA.enable();

    currentControl.controls.originalFaceToDealer.reset();
    currentControl.controls.originalFaceToFNMA.reset();

    currentControl.controls.currentBalanceExchanged.setValue(0);
    //this.clearGroup();
  }

  private clearSeparators(inputNumber: string): string {
    if (typeof inputNumber === 'string') {
      return inputNumber.replace(/,/g, '');
    } else {
      return inputNumber;
    }

    // trim number first
    //return this.replace(/(^\s*|\s*$)/g, "");
  }

  private calculateBondExchanges(indexOfCurrentControl: number): void {
    // 1. find current control within the group
    // 2. calculate other controls of the same type
    //    2a. set the value of the controls
    //    2b. set the opposite control to disable
    // 3. calculate other controls of the opposite type
    //    3a. set the value of the controls
    //    3b. set the opposite control to disable

    const currentControl = this.lookupControlByIndex(indexOfCurrentControl);
    this.calculateBondControls(currentControl, indexOfCurrentControl);
  }

  private lookupControlByIndex(index: number): BondForm {
    const recombinationRowsControl = this.recombinationGroup.controls.recombinationRows;
    return recombinationRowsControl.at(index);
  }

  // determine type
  private calculateBondControls(currentBondControl: BondForm, _indexOfCurrentControl: number) {
    const bond = currentBondControl.value as Bond;
    bond.originalFaceToFNMA =
      bond && !isNullOrUndefined(bond.originalFaceToFNMA)
        ? Number(
            this.clearSeparators(
              bond && bond.originalFaceToFNMA && bond.originalFaceToFNMA.toString(),
            ),
          )
        : null;
    bond.originalFaceToDealer =
      bond && !isNullOrUndefined(bond.originalFaceToDealer)
        ? Number(
            this.clearSeparators(
              bond && bond.originalFaceToDealer && bond.originalFaceToDealer.toString(),
            ),
          )
        : null;
    let currentBalanceOfBond: number = bond.originalFaceToFNMA || bond.originalFaceToDealer;
    currentBalanceOfBond = Number(this.clearSeparators(currentBalanceOfBond.toString()));

    const currentBondExchangeTypes = this.currentActiveBondClass(bond);
    const recombinationRowsControl = this.recombinationGroup.controls.recombinationRows;

    recombinationRowsControl.controls.filter((bondControl) => {
      if (bondControl.value.factor === 0) {
        this.markTouchedBondAsPaidOff(bondControl);
      }
    });

    if (currentBondControl.value.factor === 0) {
      this.markTouchedBondAsPaidOff(currentBondControl);
    } else {
      currentBondControl
        .get(currentBondExchangeTypes.currentBalanceOfSameBondType)
        .setValue(currentBalanceOfBond);

      // opposite type
      currentBondControl.get(currentBondExchangeTypes.currentBalanceOfOppositeBondType).setValue(0);
      currentBondControl.get(currentBondExchangeTypes.currentBalanceOfOppositeBondType).disable();
    }
  }

  private currentActiveBondClass(bond: Bond): {
    currentBalanceOfSameBondType?: string;
    currentBalanceOfOppositeBondType?: string;
  } {
    const currentBondExchangeTypes: {
      currentBalanceOfSameBondType?: string;
      currentBalanceOfOppositeBondType?: string;
    } = {};

    if (bond.originalFaceToFNMA != null) {
      currentBondExchangeTypes.currentBalanceOfSameBondType = 'originalFaceToFNMA';
      currentBondExchangeTypes.currentBalanceOfOppositeBondType = 'originalFaceToDealer';
    } else if (bond.originalFaceToDealer != null) {
      currentBondExchangeTypes.currentBalanceOfSameBondType = 'originalFaceToDealer';
      currentBondExchangeTypes.currentBalanceOfOppositeBondType = 'originalFaceToFNMA';
    }

    return currentBondExchangeTypes;
  }

  public currentBondClass(bond: Bond): string {
    return bond.rcrClass ? this.rcrClass : this.remicClass;
  }

  private currentBalanceExchangedCalculation(bond: Bond, _computedFactor: number): number {
    const exchangeTypeValue: number = bond.originalFaceToDealer || bond.originalFaceToFNMA;
    const computedBondValue: number =
      Math.ceil(bond.factor * exchangeTypeValue * 100 - 0.005) / 100;

    return computedBondValue;
  }

  public incrementBond(indexOfCurrentBondControl: number, bondClass: string): void {
    const currentBondControl = this.lookupControlByIndex(indexOfCurrentBondControl);
    const currentBond = currentBondControl.value as Bond;
    const recombinationRowsControl = this.recombinationGroup.controls.recombinationRows;

    const incrementedBondClass: Bond = this.incrementBondClass(currentBond, bondClass);
    recombinationRowsControl.at(indexOfCurrentBondControl).patchValue(incrementedBondClass);

    const incrementedBondCurrentBalanceExchanged: Bond =
      this.incrementBondCurrentBalanceExchanged(currentBond);
    recombinationRowsControl
      .at(indexOfCurrentBondControl)
      .patchValue(incrementedBondCurrentBalanceExchanged);
  }

  private incrementBondClass(bond: Bond, bondClass: string): Bond {
    if (bond.hasOwnProperty(bondClass)) {
      bond[bondClass] = this.clearSeparators(bond[bondClass]);
      bond[bondClass]++;
    }

    return bond;
  }

  private incrementBondCurrentBalanceExchanged(bond: Bond): Bond {
    const modifiedBond: Bond = bond;
    modifiedBond.currentBalanceExchanged = modifiedBond.currentBalanceExchanged + 0.1;
    bond.currentBalanceExchanged = this.currentBalanceExchangedCalculation(modifiedBond, 0);

    return bond;
  }

  public decrementBond(indexOfCurrentBondControl: number, bondClass: string): void {
    const currentBondControl = this.lookupControlByIndex(indexOfCurrentBondControl);
    const currentBond = currentBondControl.value as Bond;
    const recombinationRowsControl = this.recombinationGroup.controls.recombinationRows;

    const decrementedBondClass: Bond = this.decrementBondClass(currentBond, bondClass);
    recombinationRowsControl.at(indexOfCurrentBondControl).patchValue(decrementedBondClass);

    const decrementedBondCurrentBalanceExchanged: Bond =
      this.decrementBondBalanceExchanged(currentBond);
    recombinationRowsControl
      .at(indexOfCurrentBondControl)
      .patchValue(decrementedBondCurrentBalanceExchanged);
  }

  private decrementBondClass(bond: Bond, bondClass: string): Bond {
    if (bond.hasOwnProperty(bondClass)) {
      bond[bondClass]--;
    }

    return bond;
  }

  private decrementBondBalanceExchanged(bond: Bond): Bond {
    const modifiedBond: Bond = bond;
    modifiedBond.currentBalanceExchanged = modifiedBond.currentBalanceExchanged - 0.1;
    bond.currentBalanceExchanged = this.currentBalanceExchangedCalculation(modifiedBond, 0);

    return bond;
  }

  private markBondAsPaidOff(bondControl: BondForm): void {
    if (bondControl.controls.factor.value === 0) {
      bondControl.controls.currentBalanceExchanged.setValue(0);
      bondControl.controls.originalFaceToFNMA.setValue(null);
      bondControl.controls.originalFaceToDealer.setValue(null);
      bondControl.controls.originalFaceToFNMA.disable();
      bondControl.controls.originalFaceToDealer.disable();
      bondControl.disable();
    }
  }

  private markTouchedBondAsPaidOff(bondControl: BondForm): void {
    if (bondControl.controls.factor.value === 0) {
      bondControl.controls.currentBalanceExchanged.setValue(0);
      bondControl.controls.originalFaceToFNMA.setValue(0);
      bondControl.controls.originalFaceToDealer.setValue(0);
      bondControl.controls.originalFaceToFNMA.disable();
      bondControl.controls.originalFaceToDealer.disable();
      bondControl.disable();
    }
  }

  private resetBondControlsToDisable(): void {
    const recombinationRowsControl = this.recombinationGroup.controls.recombinationRows;
    recombinationRowsControl.controls.filter((bondControl) => {
      if (bondControl.value.originalFaceToFNMA === 0) {
        bondControl.controls.originalFaceToFNMA.disable();
      } else if (bondControl.value.originalFaceToFNMA > 0) {
        this.setIsAvailableToIncrement(true);
      }

      if (bondControl.value.originalFaceToDealer === 0) {
        bondControl.controls.originalFaceToDealer.disable();
      } else if (bondControl.value.originalFaceToDealer > 0) {
        this.setIsAvailableToIncrement(true);
      }
    });
  }

  public clearGroup(): void {
    this.setIsAvailableToIncrement(false);
    this.recombinationGroup.reset();
    this.buildBonds(this.recombinationTableGroupCopy);
    this.updateFormControlsDisabledState();
    this.cdr.detectChanges();
  }

  private setIsAvailableToIncrement(isAvailableFlag: boolean): void {
    this.isAvailableToIncrement = isAvailableFlag;
  }

  private onInitTooltipError(): void {}

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }

  private updateFormControlsDisabledState(): void {
    if (!this.recombinationGroup?.controls?.recombinationRows) {
      return;
    }

    this.recombinationGroup.controls.recombinationRows.controls.forEach((bondControl: BondForm) => {
      if (this.shouldDisableFannieMaeInput(bondControl)) {
        bondControl.controls.originalFaceToFNMA.disable();
      } else if (!bondControl.controls.originalFaceToFNMA.disabled) {
        bondControl.controls.originalFaceToFNMA.enable();
      }

      if (this.shouldDisableDealerInput(bondControl)) {
        bondControl.controls.originalFaceToDealer.disable();
      } else if (!bondControl.controls.originalFaceToDealer.disabled) {
        bondControl.controls.originalFaceToDealer.enable();
      }
    });
  }

  protected shouldDisableFannieMaeInput(bond: BondForm): boolean {
    return (
      this.isExternalUser === true &&
      this.isFromTakedownPage === true &&
      bond.controls.rcrClass.value != null
    );
  }

  protected shouldDisableDealerInput(bond: BondForm): boolean {
    return (
      this.isExternalUser === true &&
      this.isFromTakedownPage === true &&
      bond.controls.remicClass.value != null
    );
  }
}
